import { CircularProgress } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Review360AdminSession, review360Api } from 'src/api/review360';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { selectCircleDetailsData } from 'src/redux/slices/circleDetailsSlice/circleDetailsSlice';

import { FramedDashboard } from '../Oth360Tab/FramedDashboard';
import {
  ADMIN_LOADING_TEXT,
  ADMIN_UNAVAILABLE_TEXT,
  adminNotOwnerText,
} from '../Oth360Tab/oth360Text';
import { useTabActive } from '../Tabs/tabActiveContext';
import styles from './Oth360AdminTab.module.scss';

type Oth360AdminTabProps = {
  circleId: string;
};

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; session: Review360AdminSession }
  | { kind: 'error' };

/**
 * The leader's "360" tab: the 360review admin dashboard inside the circle,
 * for the leader who owns the account (docs/briefs/oth-360-admin-tab.md).
 * The admin URL is a one-hour admin credential: asked for when the tab is
 * opened (never prefetched), kept in this component's state only, used once
 * in the frame. When the hour is up the dashboard posts that its session has
 * expired and a new session is asked for once; if that one expires too, or
 * the call fails, the error card with Try again.
 */
export const Oth360AdminTab: FC<Oth360AdminTabProps> = ({ circleId }) => {
  const isActive = useTabActive();
  const circleData = useAppSelector(selectCircleDetailsData);
  const accountOwnerEmail =
    circleData?.integrations?.review360?.account_owner_email;
  const [state, setState] = useState<LoadState>({ kind: 'idle' });
  const requested = useRef(false);
  const isMounted = useRef(true);
  // One fresh session per expiry: a second expiry ends in the error card
  const renewedAfterExpiry = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const ask = useCallback(() => {
    requested.current = true;
    setState({ kind: 'loading' });
    review360Api
      .getAdminSession(circleId)
      .then((session) => {
        if (isMounted.current) {
          setState({ kind: 'ready', session });
        }
      })
      .catch(() => {
        if (isMounted.current) {
          setState({ kind: 'error' });
        }
      });
  }, [circleId]);

  // Asked once, the first time the tab is shown
  useEffect(() => {
    if (isActive && !requested.current) {
      ask();
    }
  }, [isActive, ask]);

  const tryAgain = useCallback(() => {
    renewedAfterExpiry.current = false;
    ask();
  }, [ask]);

  const onSessionExpired = useCallback(() => {
    if (renewedAfterExpiry.current) {
      if (isMounted.current) {
        setState({ kind: 'error' });
      }
      return;
    }
    renewedAfterExpiry.current = true;
    ask();
  }, [ask]);

  if (state.kind === 'idle' || state.kind === 'loading') {
    return (
      <section className={styles.container}>
        <div className={styles.loading} role="status" aria-live="polite">
          <CircularProgress
            size={40}
            sx={{ color: 'var(--brand-accent, #f0544f)' }}
          />
          <p className={styles.quiet}>{ADMIN_LOADING_TEXT}</p>
        </div>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section className={styles.container}>
        <div className={styles.card}>
          <p className={styles.quiet}>{ADMIN_UNAVAILABLE_TEXT}</p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={tryAgain}
            >
              Try again
            </button>
          </div>
        </div>
      </section>
    );
  }

  const { session } = state;
  if (!session.granted) {
    return (
      <section className={styles.container}>
        <div className={styles.card}>
          <p className={styles.text}>{adminNotOwnerText(accountOwnerEmail)}</p>
        </div>
      </section>
    );
  }

  return (
    <FramedDashboard
      src={session.adminUrl}
      title="360review admin"
      loadingText={ADMIN_LOADING_TEXT}
      onSessionExpired={onSessionExpired}
    />
  );
};
